import { createContext, useContext, useState, useCallback } from "react";
import ChatWindow from "../components/ChatWindow";

const FloatingChatContext = createContext(null);

export function useFloatingChat() {
  return useContext(FloatingChatContext);
}

/**
 * The floating chat, bottom right. One is open at a time: opening another minimizes it,
 * and minimized chats show in the Messages bar. Back returns to the bar's inbox.
 * @see components/shell/ShellMessagesBar
 */
export function FloatingChatProvider({ children, isDark, setToast, user }) {
  const [windows, setWindows] = useState([]);

  // Open chats belong to the account that opened them: after logout or a switch, none carry over.
  const accountId = user?.id || user?._id || null;
  const [windowsOwner, setWindowsOwner] = useState(accountId);
  if (windowsOwner !== accountId) {
    setWindowsOwner(accountId);
    setWindows([]);
  }

  const openChat = useCallback((conversation) => {
    if (!conversation?._id) return;
    setWindows((prev) => {
      const others = prev
        .filter((w) => w.conversation._id !== conversation._id)
        .map((w) => ({ ...w, minimized: true }));
      return [...others, { conversation, minimized: false }];
    });
  }, []);

  const minimizeChat = useCallback((conversationId) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.conversation._id === conversationId ? { ...w, minimized: true } : w
      )
    );
  }, []);

  const closeChat = useCallback((conversationId) => {
    setWindows((prev) => prev.filter((w) => w.conversation._id !== conversationId));
  }, []);

  const value = { openChat, minimizeChat, closeChat, windows };
  const openWindow = windows.find((w) => !w.minimized);

  return (
    <FloatingChatContext.Provider value={value}>
      {children}
      {user && openWindow && (
        <div
          className="pointer-events-none fixed bottom-0 right-0 z-[200] flex max-w-full flex-col items-end overflow-x-hidden p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pr-[max(0.75rem,env(safe-area-inset-right))] sm:bottom-[74px] sm:right-2 sm:p-4"
          aria-live="polite"
        >
          <div className="pointer-events-auto h-[min(500px,calc(100dvh-120px))] min-h-0 w-[min(340px,calc(100vw-1.5rem))] shrink-0 overflow-hidden rounded-radius-lg shadow-overlay">
            <ChatWindow
              key={openWindow.conversation._id}
              conversation={openWindow.conversation}
              currentUser={user}
              isDark={isDark}
              setToast={setToast}
              onBack={() => {
                minimizeChat(openWindow.conversation._id);
                window.dispatchEvent(new CustomEvent("open-inbox"));
              }}
              floating
              onClose={() => closeChat(openWindow.conversation._id)}
              onGroupLeft={() => {
                closeChat(openWindow.conversation._id);
                window.dispatchEvent(new CustomEvent("messages-updated"));
              }}
            />
          </div>
        </div>
      )}
    </FloatingChatContext.Provider>
  );
}
